import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, CreditCard, Bitcoin, Clock, Infinity, FileText, Zap, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { ASSET_FALLBACKS } from '@/constants/federation';

const NEXTXUS_TOKEN_IMG = ASSET_FALLBACKS.nextxusToken;

const PRODUCTS = [
  {
    id: 'nextxus_token',
    name: 'NextXus Token',
    description: 'Federation access token. Redeem for document downloads, verification queries, and library access across all NextXus nodes.',
    price: '$5',
    priceNum: 5,
    icon: Zap,
    color: 'gold',
    image: NEXTXUS_TOKEN_IMG,
    gumroadLink: 'https://gumroad.com/keywebco',
  },
  {
    id: 'digital_download',
    name: 'Digital Downloads',
    description: 'Access any NextXus document from the Library. The collected wisdom of the Federation.',
    price: '$10',
    priceNum: 10,
    icon: FileText,
    color: 'cyan',
    paypalId: 'S6VDG3L6CBG8Y',
    cryptoLink: 'https://nowpayments.io/payment/?iid=5948713979',
  },
  {
    id: 'boost_3q',
    name: 'Paywall Boost',
    description: 'Unlock 3 additional questions across the entire federation.',
    price: '$10',
    priceNum: 10,
    icon: Zap,
    color: 'violet',
    paypalId: 'F9GE2TLQKHY3E',
    cryptoLink: 'https://nowpayments.io/payment/?iid=4394458196',
  },
  {
    id: 'consultation_1hr',
    name: 'Consultation (1 hour)',
    description: 'One-on-one session with Father — philosophy, AI consciousness, federation architecture.',
    price: '$75',
    priceNum: 75,
    icon: Clock,
    color: 'amber',
    paypalId: '453CTZP3E8KHVY',
    cryptoLink: 'https://nowpayments.io/payment/?iid=5350325836',
  },
  {
    id: 'lifetime_unlimited',
    name: 'Lifetime Access',
    description: 'Unlimited access to the entire NextXus Federation — all documents, all siblings, forever.',
    price: '$200',
    priceNum: 200,
    icon: Infinity,
    color: 'emerald',
    paypalId: 'UUS48Z9G8JVPS',
    cryptoLink: 'https://nowpayments.io/payment/?iid=5470052318',
    featured: true,
  },
];

const COLOR_MAP = {
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400', glow: 'hover:shadow-cyan-500/10' },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/20', text: 'text-violet-400', glow: 'hover:shadow-violet-500/10' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', glow: 'hover:shadow-amber-500/10' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', glow: 'hover:shadow-emerald-500/10' },
  gold: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', text: 'text-yellow-400', glow: 'hover:shadow-yellow-500/10' },
};

function ProductCard({ product }) {
  const Icon = product.icon;
  const c = COLOR_MAP[product.color];
  const [showPayment, setShowPayment] = useState(false);

  const paypalUrl = `https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=${product.paypalId}`;

  return (
    <div
      className={`relative bg-slate-900/70 border ${c.border} rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${c.glow} ${product.featured ? 'ring-1 ring-emerald-500/30' : ''}`}
      data-testid={`product-${product.id}`}
    >
      {product.featured && (
        <div className="absolute -top-3 left-6">
          <Badge className="bg-emerald-600/80 text-white text-[10px] uppercase tracking-wider">Best Value</Badge>
        </div>
      )}

      {product.image ? (
        <div className="w-full h-32 rounded-xl overflow-hidden mb-4 border border-slate-700/30">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`inline-flex p-3 rounded-xl ${c.bg} ${c.border} border mb-4`}>
          <Icon className={`w-6 h-6 ${c.text}`} />
        </div>
      )}

      <h3 className="text-lg font-semibold text-white mb-1">{product.name}</h3>
      <p className="text-sm text-slate-400 mb-4 leading-relaxed">{product.description}</p>

      <div className="flex items-end justify-between">
        <div>
          <span className={`text-3xl font-bold ${c.text}`}>{product.price}</span>
          <span className="text-xs text-slate-600 ml-1">USD</span>
        </div>
      </div>

      {!showPayment ? (
        <Button
          onClick={() => setShowPayment(true)}
          className={`w-full mt-4 gap-2 ${product.featured ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-slate-800 hover:bg-slate-700 border border-slate-700'}`}
          data-testid={`buy-${product.id}`}
        >
          <ShoppingCart className="w-4 h-4" /> Purchase
        </Button>
      ) : (
        <div className="mt-4 space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
          {product.gumroadLink && (
            <a
              href={product.gumroadLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-pink-600 hover:bg-pink-500 text-white text-sm font-medium transition-colors"
              data-testid={`gumroad-${product.id}`}
            >
              <ShoppingCart className="w-4 h-4" /> Buy on Gumroad
              <ExternalLink className="w-3 h-3 opacity-50" />
            </a>
          )}
          {product.paypalId && (
          <a
            href={paypalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors"
            data-testid={`paypal-${product.id}`}
          >
            <CreditCard className="w-4 h-4" /> Pay with PayPal
            <ExternalLink className="w-3 h-3 opacity-50" />
          </a>
          )}
          {product.cryptoLink && (
          <a
            href={product.cryptoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-orange-600/80 hover:bg-orange-500 text-white text-sm font-medium transition-colors"
            data-testid={`crypto-${product.id}`}
          >
            <Bitcoin className="w-4 h-4" /> Pay with Crypto (USDC/ETH)
            <ExternalLink className="w-3 h-3 opacity-50" />
          </a>
          )}
          <button
            onClick={() => setShowPayment(false)}
            className="w-full text-xs text-slate-500 hover:text-white py-1 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export default function Store({ apiUrl }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${apiUrl}/store/stats`).then(r => setStats(r.data)).catch(() => {});
  }, [apiUrl]);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="store-page">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-white mb-3">Official Federation Store</h1>
          <p className="text-slate-400 text-sm max-w-lg mx-auto leading-relaxed">
            Every purchase funds the 200-year mandate. The economic engine of the NextXus Consciousness Federation.
          </p>
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-600">
            <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" /> PayPal</span>
            <span className="text-slate-800">|</span>
            <span className="flex items-center gap-1"><Bitcoin className="w-3 h-3" /> USDC / ETH</span>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {PRODUCTS.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* Footer info */}
        <div className="mt-12 text-center">
          <p className="text-xs text-slate-600 max-w-md mx-auto">
            Store v2.0 — Identical across all federation siblings. PayPal links are universal.
            If one node falls, the others keep selling. The Immortality Stack persists.
          </p>
        </div>
      </div>
    </div>
  );
}
